import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate } from 'react-router';
import { z } from 'zod';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@bekebi/domain';
import { ApiError } from '../api/client';
import { Button } from '../components/button';
import { BekebiMark } from '../components/brand';
import { ErrorState, LoadingState } from '../components/states';
import { useCreateFirstAdmin, useSetupStatus } from '../features/setup/api';
import type { LoginLocationState } from '../session/auth-actions';

/**
 * First-run setup (/setup): creates the portal's first Admin while none exists. Authorised by the
 * one-time setup secret the server operator puts in the server environment (PORTAL_SETUP_TOKEN):
 * a visitor who does not hold it cannot create an account. The server checks the secret, the
 * email and the password policy, and refuses everything once an Admin exists — this page is only
 * the guide. Nothing typed here is stored, logged or put in a URL.
 */

const setupFormSchema = z
  .object({
    setupToken: z.string().trim().min(1, 'Enter the setup token from the server operator.'),
    email: z
      .string()
      .trim()
      .min(1, 'Enter the administrator’s email address.')
      .max(254, 'That email address is too long.')
      .email('Enter a valid email address.'),
    password: z
      .string()
      .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
      .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters.`),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match.',
  });
type SetupForm = z.infer<typeof setupFormSchema>;

function setupErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (
      [
        'invalid_setup_token',
        'setup_complete',
        'setup_unavailable',
        'conflict',
        'unprocessable',
        'rate_limited',
        'network_error',
      ].includes(error.code)
    ) {
      return error.message;
    }
    if (error.status === 400) return 'Check the setup token, email address and password.';
  }
  return 'The administrator could not be created. Try again in a moment.';
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-canvas">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(ellipse_55%_50%_at_50%_0%,rgb(206_77_31/0.2),rgb(230_158_50/0.06)_55%,transparent)]"
      />
      <div className="relative flex flex-1 items-start justify-center px-4 py-10 sm:items-center sm:py-12">
        <div className="w-full max-w-[28rem] rounded-2xl border border-line bg-surface px-6 py-8 shadow-2xl shadow-black/40 sm:px-9 sm:py-10">
          <div className="flex justify-center">
            <BekebiMark size={64} />
          </div>
          <h1 className="mt-4 text-center">Set up Bekebi Portal</h1>
          {children}
        </div>
      </div>
    </main>
  );
}

/** Where to go once the first Admin exists: sign-in, with a confirmation. */
const CREATED_STATE: LoginLocationState = { reason: 'setup_complete' };

export function SetupPage() {
  const status = useSetupStatus();
  const [created, setCreated] = useState(false);

  // Checked first: after creation the status also reads "not required", which on its own would
  // send the visitor to sign-in without the confirmation.
  if (created) return <Navigate to="/login" replace state={CREATED_STATE} />;
  if (status.isPending) return <LoadingState label="Checking the portal…" />;
  if (status.isError) {
    return (
      <Card>
        <div className="mt-6">
          <ErrorState
            title="The portal could not be checked"
            message={status.error.message}
            onRetry={() => void status.refetch()}
          />
        </div>
      </Card>
    );
  }
  // An Admin exists: setup is closed for good.
  if (!status.data.setupRequired) return <Navigate to="/login" replace />;
  return (
    <Card>
      <p className="mt-2 text-center text-sm text-muted">
        This portal has no administrator yet. Create the first one here.
      </p>
      {status.data.setupAvailable ? (
        <SetupForm onCreated={() => setCreated(true)} />
      ) : (
        <OperatorSteps onCheckAgain={() => void status.refetch()} checking={status.isFetching} />
      )}
    </Card>
  );
}

/** Shown while the server has no setup secret: what the server operator has to do. */
function OperatorSteps({
  onCheckAgain,
  checking,
}: {
  onCheckAgain: () => void;
  checking: boolean;
}) {
  return (
    <section aria-labelledby="operator-steps" className="mt-7">
      <h2 id="operator-steps" className="text-sm font-semibold text-fg">
        First, the server operator enables setup
      </h2>
      <p className="mt-2 text-sm text-fg-2">
        Creating the first administrator needs a one-time setup token that only the server operator
        can set, so that nobody else can claim this portal.
      </p>
      <ol className="mt-4 list-decimal space-y-3 pl-5 text-sm text-fg-2">
        <li>
          On the server, generate a token:
          <code className="mt-1.5 block w-fit whitespace-nowrap rounded bg-raised px-2 py-1 font-mono text-xs text-fg">
            openssl rand -hex 32
          </code>
        </li>
        <li>
          Set it as{' '}
          <code className="rounded bg-raised px-1.5 py-0.5 font-mono text-xs text-fg">
            PORTAL_SETUP_TOKEN
          </code>{' '}
          in the portal’s server environment (Dokploy → Environment) and redeploy.
        </li>
        <li>Come back to this page and enter the token with the administrator’s details.</li>
      </ol>
      <Button
        variant="secondary"
        className="mt-6 h-11 w-full"
        onClick={onCheckAgain}
        disabled={checking}
      >
        {checking ? 'Checking…' : 'Check again'}
      </Button>
      <p className="mt-7 border-t border-line pt-5 text-center text-xs text-muted">
        Once an administrator exists, this page closes for good and accounts are created by an
        administrator.
      </p>
    </section>
  );
}

function SetupForm({ onCreated }: { onCreated: () => void }) {
  const create = useCreateFirstAdmin();
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<SetupForm>({ resolver: zodResolver(setupFormSchema) });

  const onSubmit = handleSubmit((values) => {
    create.mutate(
      { setupToken: values.setupToken, email: values.email, password: values.password },
      {
        onSuccess: onCreated,
        onSettled: () => {
          resetField('password');
          resetField('confirmPassword');
        },
      },
    );
  });

  const field = (
    name: keyof SetupForm,
    label: string,
    type: string,
    autoComplete: string,
    hint?: string,
  ) => {
    const error = errors[name];
    const describedBy = [hint ? `${name}-hint` : null, error ? `${name}-error` : null]
      .filter(Boolean)
      .join(' ');
    return (
      <div>
        <label htmlFor={name} className="block text-sm font-medium text-fg">
          {label}
        </label>
        <input
          id={name}
          type={type}
          autoComplete={autoComplete}
          spellCheck={false}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedBy || undefined}
          className="mt-1.5 block h-11 w-full border px-3.5 text-sm"
          {...register(name)}
        />
        {hint ? (
          <p id={`${name}-hint`} className="mt-1.5 text-xs text-muted">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={`${name}-error`} className="mt-1.5 text-sm text-danger">
            {error.message}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {create.isError ? (
        <p
          role="alert"
          className="mt-6 rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          {setupErrorMessage(create.error)}
        </p>
      ) : null}
      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-7 space-y-5"
        aria-label="Create the first administrator"
      >
        {field(
          'setupToken',
          'Setup token',
          'password',
          'off',
          'The one-time token the server operator set (PORTAL_SETUP_TOKEN).',
        )}
        {field('email', 'Administrator email', 'email', 'username')}
        {field(
          'password',
          'Password',
          'password',
          'new-password',
          `At least ${PASSWORD_MIN_LENGTH} characters.`,
        )}
        {field('confirmPassword', 'Confirm password', 'password', 'new-password')}
        <Button type="submit" className="h-11 w-full" disabled={create.isPending}>
          {create.isPending ? 'Creating administrator…' : 'Create administrator'}
        </Button>
      </form>
      <p className="mt-7 border-t border-line pt-5 text-center text-xs text-muted">
        This creates the first administrator only. After that, this page closes for good and
        accounts are created by an administrator.
      </p>
    </>
  );
}
